import { useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Info,
  MapPin,
} from 'lucide-react'
import type {
  ChatMessage,
  MediaPdf,
  MediaParking,
  MediaResult,
  MediaSummary,
  MediaWordForWord,
  SummaryIcon,
} from '@/lib/library/mediaResultContent'

/**
 * The rendering half of `lib/library/mediaResultContent.ts`.
 *
 * Lifted out of ./MediaResultModal.tsx because the modal was the ONLY surface
 * that knew how to draw a structured image or PDF result. A translation the
 * user had just run went to the translator's target pane instead, which only
 * ever renders prose — so a PDF came back as one undifferentiated block of
 * text until you reopened it from History. The parse and the render are both
 * shared now: the fresh result and the history row draw the same thing.
 *
 * Presentational only. No fetch, no storage, no gates — the modal keeps its
 * Copy/Listen/Save actions and their Pro gating.
 */

const KEY_POINT_ICONS: Record<SummaryIcon, typeof Info> = {
  time: Clock,
  place: MapPin,
  action: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
}

/** Small uppercase label above each block, matching the panel headers on the
 * Translate page. */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-[11px] font-bold uppercase tracking-wider mt-4 mb-1.5"
      style={{ color: 'var(--muted-2)' }}
    >
      {children}
    </div>
  )
}

export function Prose({ children }: { children: string }) {
  return (
    <div
      dir="auto"
      style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
    >
      {children}
    </div>
  )
}

/**
 * A translated chat screenshot, drawn the way the screenshot looked: received
 * bubbles on the left, the owner's own on the right, a sender name above a
 * bubble when the chat showed one (group chats) and the time the bubble
 * carried. The translation of a conversation is only readable as a
 * conversation — as a paragraph it loses who said what, which is the whole
 * point of translating it.
 */
function ChatTranscriptView({ messages }: { messages: ChatMessage[] }) {
  return (
    <div className="flex flex-col" role="list" aria-label="Conversation">
      {messages.map((m, i) => {
        const mine = m.side === 'me'
        const prev = messages[i - 1]
        // WhatsApp groups consecutive bubbles from one side tightly, and
        // repeats the sender name only when the speaker changes.
        const continues = !!prev && prev.side === m.side && prev.sender === m.sender
        const showSender = !!m.sender && !continues
        return (
          <div
            key={i}
            role="listitem"
            className={mine ? 'flex justify-end' : 'flex justify-start'}
            style={{ marginTop: i === 0 ? 0 : continues ? 3 : 10 }}
          >
            <div
              className="rounded-lg"
              style={{
                maxWidth: '78%',
                padding: '7px 10px 6px',
                background: mine ? 'var(--accent-soft)' : 'var(--card-2)',
                border: `1px solid ${mine ? 'var(--accent-soft)' : 'var(--line)'}`,
                borderTopLeftRadius: !mine && continues ? 6 : undefined,
                borderTopRightRadius: mine && continues ? 6 : undefined,
              }}
            >
              {showSender && (
                <div
                  dir="auto"
                  style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--accent-deep)', marginBottom: 2 }}
                >
                  {m.sender}
                </div>
              )}
              <div
                dir="auto"
                style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.45, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
              >
                {m.text}
              </div>
              {m.time && (
                <div
                  className="font-mono"
                  style={{ fontSize: 10, color: 'var(--muted-2)', marginTop: 3, textAlign: 'end' }}
                  dir="ltr"
                >
                  {m.time}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}


/**
 * Full translation / Summary, as two tabs rather than one long scroll.
 *
 * An image result carried the gist, the key points and then the whole
 * translation stacked vertically, so the thing most people opened the photo
 * for was three screens down. The translation leads now and the summary is
 * one click away, both always reachable without scrolling past the other.
 */
function ResultTabs({
  tabs,
}: {
  tabs: { id: string; label: string; content: React.ReactNode }[]
}) {
  const [active, setActive] = useState(tabs[0]?.id)
  const current = tabs.find((t) => t.id === active) ?? tabs[0]
  if (tabs.length === 1) return <>{tabs[0].content}</>
  return (
    <>
      <div
        role="tablist"
        aria-label="Result view"
        className="flex items-center gap-1 mt-4 p-0.5 rounded-md w-fit"
        style={{ background: 'var(--card-2)', border: '1px solid var(--line)' }}
      >
        {tabs.map((tab) => {
          const selected = tab.id === current?.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(tab.id)}
              className="rounded transition-colors duration-150 cursor-pointer touch-44-y"
              style={{
                height: 26,
                padding: '0 10px',
                fontSize: 12.5,
                fontWeight: selected ? 600 : 500,
                background: selected ? 'var(--card)' : 'transparent',
                color: selected ? 'var(--ink)' : 'var(--muted)',
                boxShadow: selected ? 'var(--shadow-sm)' : undefined,
              }}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
      <div role="tabpanel" className="mt-1">{current?.content}</div>
    </>
  )
}

/**
 * The image "Standard" result, mirroring baba-v2
 * `mobile/components/image/redesign/SummaryResult.tsx`: an optional source
 * label and title, the TL;DR, the key points, then the full translation when
 * the model returned one. Mobile draws a uniform dot for every key point;
 * the web has room for the semantic icon the payload already carries.
 */
function SummaryView({ data }: { data: MediaSummary }) {
  const summaryPanel = (
    <>
      <SectionLabel>What It Says</SectionLabel>
      <div
        className="rounded-md"
        style={{ background: 'var(--card-2)', border: '1px solid var(--line)', padding: 12 }}
      >
        <Prose>{data.tldr}</Prose>
      </div>

      {data.keyPoints.length > 0 && (
        <>
          <SectionLabel>Key Points</SectionLabel>
          <div
            className="rounded-md [&>*+*]:border-t [&>*+*]:border-[var(--line-hair)]"
            style={{ border: '1px solid var(--line)' }}
          >
            {data.keyPoints.map((point, i) => {
              const PointIcon = KEY_POINT_ICONS[point.icon]
              return (
                <div
                  key={`${point.title}-${i}`}
                  className="grid items-start gap-2.5"
                  style={{ gridTemplateColumns: '16px minmax(0,1fr)', padding: '10px 12px' }}
                >
                  <span style={{ color: 'var(--accent-deep)', marginTop: 1 }}>
                    <PointIcon size={14} strokeWidth={1.8} />
                  </span>
                  <span className="min-w-0">
                    <span dir="auto" className="block" style={{ fontSize: 13, color: 'var(--ink)' }}>
                      {point.title}
                    </span>
                    {point.sub && (
                      <span
                        dir="auto"
                        className="block"
                        style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 1 }}
                      >
                        {point.sub}
                      </span>
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </>
  )

  // A conversation screenshot has a transcript instead of prose; either one is
  // "the translation" for tab purposes.
  const translationPanel =
    data.messages.length > 0 ? (
      <div className="mt-2.5">
        <ChatTranscriptView messages={data.messages} />
      </div>
    ) : data.fullTranslation.trim() ? (
      <div className="mt-2.5">
        <Prose>{data.fullTranslation}</Prose>
      </div>
    ) : null

  const tabs = [
    ...(translationPanel
      ? [{ id: 'translation', label: data.messages.length > 0 ? 'Conversation' : 'Full Translation', content: translationPanel }]
      : []),
    { id: 'summary', label: 'Summary', content: summaryPanel },
  ]

  return (
    <>
      {data.sourceLabel && (
        <div className="font-mono" style={{ fontSize: 11, color: 'var(--muted-2)' }}>
          {data.sourceLabel}
        </div>
      )}
      {data.title && (
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginTop: 2 }}>{data.title}</h2>
      )}
      {typeof data.wordCount === 'number' && (
        <div className="font-mono" style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 2 }}>
          {data.wordCount} {data.wordCount === 1 ? 'Word' : 'Words'}
        </div>
      )}

      <ResultTabs tabs={tabs} />
    </>
  )
}

const PARKING_VERDICT_LABEL: Record<MediaParking['verdict'], string> = {
  ok: 'You Can Park',
  no: 'Do Not Park',
  caution: 'Check The Details',
}

function ParkingView({ data }: { data: MediaParking }) {
  return (
    <>
      {data.signLabel && (
        <div className="font-mono" style={{ fontSize: 11, color: 'var(--muted-2)' }}>
          {data.signLabel}
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--muted-2)', marginTop: 2 }}>
        {PARKING_VERDICT_LABEL[data.verdict].toUpperCase()}
      </div>
      <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', marginTop: 2 }}>{data.headline}</h2>
      {data.detail && (
        <div style={{ marginTop: 6 }}>
          <Prose>{data.detail}</Prose>
        </div>
      )}
      {(data.validUntil || data.timeRemaining) && (
        <div className="font-mono" style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 6 }}>
          {[data.validUntil && `Until ${data.validUntil}`, data.timeRemaining && `${data.timeRemaining} left`]
            .filter(Boolean)
            .join(' · ')}
        </div>
      )}
      {data.schedule.length > 0 && (
        <>
          <SectionLabel>Schedule</SectionLabel>
          <div
            className="rounded-md [&>*+*]:border-t [&>*+*]:border-[var(--line-hair)]"
            style={{ border: '1px solid var(--line)' }}
          >
            {data.schedule.map((row, i) => (
              <div key={`${row.label}-${i}`} style={{ padding: '8px 12px', opacity: row.muted ? 0.6 : 1 }}>
                <div style={{ fontSize: 12.5, color: 'var(--ink)' }}>{row.label}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'pre-wrap' }}>{row.value}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

function WordForWordView({ data }: { data: MediaWordForWord }) {
  return (
    <div
      className="rounded-md [&>*+*]:border-t [&>*+*]:border-[var(--line-hair)]"
      style={{ border: '1px solid var(--line)' }}
    >
      {data.pairs.map((pair, i) => (
        <div key={`${pair.he}-${i}`} className="flex items-baseline justify-between gap-3" style={{ padding: '8px 12px' }}>
          <span dir="rtl" style={{ fontSize: 13.5, color: 'var(--ink)' }}>{pair.he}</span>
          <span dir="ltr" style={{ fontSize: 12.5, color: 'var(--muted)' }}>{pair.en}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * A paged PDF layout, page by page.
 *
 * The model is told to keep `source[i]` and `target[i]` describing the same
 * paragraph (baba-v2 `server/translate-pdf/index.ts` `buildLayoutPrompt`), so
 * each row here is one paragraph with its original beside it. The phone splits
 * the same data across two swipeable cards because it has one column to work
 * with; a browser has two, so the pairing is visible without a gesture.
 *
 * `dir="auto"` per paragraph, not per document: a page mixing Hebrew and Latin
 * paragraphs has to align each one on its own.
 */
function PdfView({ data }: { data: MediaPdf }) {
  return (
    <>
      {data.generatedName && (
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)' }}>{data.generatedName}</h2>
      )}
      {data.pages.map((page, i) => {
        const rows = Math.max(page.target.length, page.source.length)
        return (
          <div key={page.index ?? i}>
            <SectionLabel>
              Page {(page.index ?? i) + 1}
              {page.heading ? ` · ${page.heading}` : ''}
            </SectionLabel>
            <div
              className="rounded-md [&>*+*]:border-t [&>*+*]:border-[var(--line-hair)]"
              style={{ border: '1px solid var(--line)' }}
            >
              {Array.from({ length: rows }, (_, r) => (
                <div
                  key={r}
                  className="grid gap-x-4 gap-y-1 md:grid-cols-2"
                  style={{ padding: '10px 12px' }}
                >
                  <div
                    dir="auto"
                    className="md:order-2"
                    style={{ fontSize: 14, color: 'var(--ink)', lineHeight: 1.55 }}
                  >
                    {page.target[r] ?? ''}
                  </div>
                  <div
                    dir="auto"
                    className="md:order-1 md:border-r md:pr-4"
                    style={{
                      fontSize: 13,
                      color: 'var(--muted)',
                      lineHeight: 1.55,
                      borderColor: 'var(--line-hair)',
                    }}
                  >
                    {page.source[r] ?? ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </>
  )
}

export function MediaResultBody({ result }: { result: MediaResult }) {
  switch (result.kind) {
    case 'summary':
      return <SummaryView data={result} />
    case 'parking':
      return <ParkingView data={result} />
    case 'word-for-word':
      return <WordForWordView data={result} />
    case 'pdf':
      return <PdfView data={result} />
    case 'plain':
      return <Prose>{result.text || 'No text detected'}</Prose>
  }
}
